// Shared config helpers for the live-chat surface. The Emblem messaging
// BE extension emits these keys on POST /v1/live-chat; the bridge uses
// them to gate + route, the warm-handoff adapter uses them to compose
// the transfer's custom attributes.

export const CONFIG_KEY_LIVE_CHAT_AVAILABLE = "LIVE_CHAT_AVAILABLE";
export const CONFIG_KEY_LIVE_CHAT_ENTITLED = "LIVE_CHAT_ENTITLED";
export const CONFIG_KEY_DEFAULT_PROVIDER = "DEFAULT_PROVIDER";

// Keys stripped from the START_CHAT config before it's handed to Genesys
// as warm-transfer custom attributes. Everything else — LeagueID, plan,
// referring page, pre-chat answers — is agent-visible context.
export const INTERNAL_CONFIG_KEYS: ReadonlySet<string> = new Set([
  CONFIG_KEY_LIVE_CHAT_AVAILABLE,
  CONFIG_KEY_LIVE_CHAT_ENTITLED,
  CONFIG_KEY_DEFAULT_PROVIDER,
]);

/**
 * Reads the `LIVE_CHAT_AVAILABLE` gate from the tenant's live-chat
 * configuration. Absent means allowed — the key only arrives on the
 * POST /v1/live-chat response, so a warm handoff (or an environment
 * whose extension predates the gate) must not be blocked by its absence.
 */
export function isOutsideHoursOfOperation(
  config: { [key: string]: unknown } | undefined
): boolean {
  const raw = config?.[CONFIG_KEY_LIVE_CHAT_AVAILABLE];
  // BE may send the gate as a boolean or a stringified boolean depending on
  // how the messaging extension serialises.
  if (typeof raw === "boolean") return !raw;
  return typeof raw === "string" && raw.toLowerCase() === "false";
}

/**
 * Reads the `LIVE_CHAT_ENTITLED` flag from the tenant's live-chat
 * configuration. Entitlement = member's LOB is serviced by live chat at
 * all (time-independent), split from availability (queue open now) by
 * EMBLEM-8962. Drives whether the chat bubble renders — out-of-hours
 * members of a staffed LOB still see the bubble, so they can reach the
 * "currently closed" surface instead of silently losing the entry point.
 *
 * Absent means allowed — EMBLEM-8962 omits the key when the extension
 * can't resolve the member's plans (ESB failure, etc.), and the AC
 * requires an unresolved lookup not to hide the bubble.
 */
export function isEntitledForLiveChat(
  config: { [key: string]: unknown } | undefined
): boolean {
  const raw = config?.[CONFIG_KEY_LIVE_CHAT_ENTITLED];
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.toLowerCase() !== "false";
  return true;
}

export function stripInternalConfigKeys(
  config: { [key: string]: unknown } | undefined
): Record<string, unknown> | undefined {
  if (!config) return undefined;
  const entries = Object.entries(config).filter(
    ([key]) => !INTERNAL_CONFIG_KEYS.has(key)
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

// Budget for the whole staged custom-attribute map, in bytes (keys and
// values together). Not the Genesys protocol's 12 KiB — this bounds what
// Emblem's deployment accepts. Over it, Genesys drops the WHOLE map.
export const MAX_CONTEXT_ATTRIBUTE_BYTES = 2048;

// The member routing attributes Emblem's Architect flow places the member
// on. Server-owned (backend-extensions live_chat_participant_data.go) and
// mirrored here only so they survive an over-budget map; keep in sync with
// the Android and iOS lists.
export const ROUTING_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  "MEMBER IS AUTHENTICATED",
  "MEMBER FIRST NAME",
  "MEMBER LAST NAME",
  "Member ID",
  "Member DOB",
]);

export function utf8Size(record: Record<string, unknown>): number {
  const encoder = new TextEncoder();
  return Object.entries(record).reduce((total, [key, value]) => {
    const valueStr = typeof value === "string" ? value : String(value);
    return total + encoder.encode(key).length + encoder.encode(valueStr).length;
  }, 0);
}

/**
 * The attributes as given when they fit the budget; otherwise only the
 * routing attributes. Genesys would drop an over-budget map whole, so the
 * chat would start with no member identity to route on — losing the
 * agent-visible context is the lesser cost. Only key names are logged:
 * the values are member PHI.
 */
export function keepRoutingAttributesWithinBudget(
  attributes: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!attributes) return undefined;
  const size = utf8Size(attributes);
  if (size <= MAX_CONTEXT_ATTRIBUTE_BYTES) return attributes;

  const routing = Object.fromEntries(
    Object.entries(attributes).filter(([key]) =>
      ROUTING_ATTRIBUTE_KEYS.has(key)
    )
  );
  const dropped = Object.keys(attributes)
    .filter((key) => !ROUTING_ATTRIBUTE_KEYS.has(key))
    .sort();
  // eslint-disable-next-line no-console
  console.warn(
    `[live-chat] custom attributes over budget (${size} bytes); staging the member routing attributes only. Dropped: ${dropped.join(
      ", "
    )}`
  );
  return Object.keys(routing).length > 0 ? routing : undefined;
}
